import * as Sentry from "@sentry/react-native";
import { Alert } from "react-native";

import { ApiBase } from "./ApiBase";

import { NetworkError } from "@/schemas/api/error";
import {
  getOrRefreshSessionFromSecureStore,
  isSessionExpired,
} from "@/context/auth/helper";
import { type AppUserSession } from "@/schemas/auth/session";

class UserApiBase extends ApiBase {
  protected session: AppUserSession;

  public constructor(subPath: string, session: AppUserSession) {
    super(subPath);
    this.session = session;
  }

  protected async handleError(error: Error): Promise<never> {
    if (error instanceof NetworkError) {
      Alert.alert(
        "Connection Error",
        "Please check your internet connection and try again.",
      );
    }
    throw error;
  }

  /**
   * Build the appropriate fetch options for the user-based API,
   * which includes the Bearer token in Authorization header.
   */
  protected buildFetchOptions(method: string, data?: unknown): RequestInit {
    const headers = {
      Authorization: `Bearer ${this.session.token.accessToken}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const options: RequestInit = {
      method,
      headers,
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    return options;
  }

  /**
   * We override `doRequest` so we can perform a token refresh
   * before every request.
   */
  protected override async doRequest<T>(
    subUrl: string,
    method: string,
    data?: unknown,
  ): Promise<T> {
    try {
      await this.checkToRefreshToken();
      return super.doRequest<T>(subUrl, method, data);
    } catch (error) {
      return this.handleError(error as Error);
    }
  }

  private async checkToRefreshToken(): Promise<void> {
    if (isSessionExpired(this.session)) {
      const newSession = await getOrRefreshSessionFromSecureStore(this.session);
      this.session = newSession;
    }
  }

  /**
   * Optionally override `returnOrRaise` to add your Sentry logic or
   * custom error handling for user requests.
   */
  protected override async returnOrRaise<T>(
    url: string,
    response: Response,
  ): Promise<T> {
    if (response.ok) {
      return await response.json();
    } else {
      let errorData: object;

      try {
        errorData = await response.json();
      } catch {
        errorData = { error: "Failed to parse response body." };
      }

      const error = new Error(`HTTP error ${response.status} for ${url}`, {
        cause: errorData,
      });
      this.raiseSentryException(response, errorData, error);
      throw error;
    }
  }

  protected override raiseSentryException(
    response: Response,
    errorData: object,
    error: Error,
  ): void {
    Sentry.withScope((scope) => {
      scope.setContext("username", {
        username: this.session.identity.username,
      });

      // scope.setContext("requestHeaders", this.buildFetchOptions("GET")); // or store them elsewhere

      scope.setContext("response", {
        errorData,
        headers: Object.fromEntries(response.headers.entries()),
        redirected: response.redirected,
        statusCode: response.status,
        statusText: response.statusText,
        url: response.url,
      });

      Sentry.captureException(error);
    });
  }
}

export default UserApiBase;
